import React, { useEffect, useId, useRef, useState, type JSX } from 'react';

interface ITabsProps {
  children: React.ReactNode;
}

interface ITabPanelProps {
  children: React.ReactNode;
  label: string;
  tabId?: number;
  string?: number;
  tabPrefix?: string;
  isTabSelected?: boolean;
}

const TabPanel: React.FC<ITabPanelProps> = ({
  children,
  tabId,
  tabPrefix = '',
  isTabSelected = false,
}) => {
  if (!tabId) return null;

  return (
    <div
      id={`${tabPrefix}tabpanel-${tabId}`}
      role="tabpanel"
      className={`tab-panel tw-text-start ${isTabSelected ? '' : 'tw-hidden'}`}
      tabIndex={0}
      aria-labelledby={`${tabPrefix}tab-${tabId}`}
    >
      {children}
    </div>
  );
};

const Tabs: React.FC<ITabsProps> = ({ children }) => {
  const tabsId = useId();
  const tabPrefix = `tab${tabsId}`;
  const [selectedTab, setSelectedTab] = useState(1);
  const [tabBgWidth, setTabBgWidth] = useState(0);
  const [tabBgLeft, setTabBgLeft] = useState(0);
  const [tabBgHeight, setTabBgHeight] = useState(0);
  const [tabBgTop, setTabBgTop] = useState(0);
  const tabListRef = useRef<HTMLButtonElement | null[]>([]);
  const TabsContent = Array.isArray(children) ? children : [children];

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowRight') {
      const nextTab = index === TabsContent.length ? 1 : index + 1;

      tabListRef.current[nextTab - 1].focus();
      tabListRef.current[nextTab - 1].click();
    } else if (e.key === 'ArrowLeft') {
      const prevTab = index === 1 ? TabsContent.length : index - 1;

      tabListRef.current[prevTab - 1].focus();
      tabListRef.current[prevTab - 1].click();
    }
  };

  const setTabPosition = () => {
    const currentTab = tabListRef.current[selectedTab - 1];

    setTabBgLeft(currentTab?.offsetLeft ?? 0);
    setTabBgWidth(currentTab?.clientWidth ?? 0);
    setTabBgTop(currentTab?.offsetTop ?? 0);
    setTabBgHeight(currentTab?.clientHeight ?? 0);
  };

  useEffect(() => {
    setTabPosition();
  }, [selectedTab]);

  useEffect(() => {
    document.fonts.ready.then(() => setTabPosition());

    window.addEventListener('resize', setTabPosition);

    return () => {
      window.removeEventListener('resize', setTabPosition);
    };
  }, []);

  return (
    <>
      <div className="tabs tw-text-center">
        <div
          role="tablist"
          className={`
            tabs-menu
            tw-relative tw-mb-12 tw-inline-block tw-rounded-full
            tw-bg-white tw-p-1 desktop:tw-p-2
          `}
        >
          <span
            className={`
              tabs-btn-bg
              tw-lf-button tw-lf-button-primary tw-lf-button-sm tw-absolute
              tw-bottom-0 tw-right-0 tw-top-0 tw-transition-all
              tw-duration-500 tw-ease-out hover:tw-bg-yellow-600
            `}
            style={{
              top: tabBgTop,
              left: tabBgLeft,
              width: tabBgWidth,
              height: tabBgHeight,
            }}
          ></span>
          {React.Children.map(TabsContent, (tabContent: JSX.Element, index) => {
            const isSelected = selectedTab === index + 1;

            return (
              <button
                ref={(ref) => {
                  if (ref) tabListRef.current[index] = ref;
                }}
                id={`${tabPrefix}tab-${index + 1}`}
                type="button"
                role="tab"
                aria-selected={isSelected ? 'true' : 'false'}
                aria-controls={`${tabPrefix}tabpanel-${index + 1}`}
                tabIndex={isSelected ? 0 : -1}
                onClick={() => setSelectedTab(index + 1)}
                onKeyDown={(e) => handleKeyDown(e, index + 1)}
                className={`
                  tabs-btn hover:tw-text-black tw-lf-button tw-lf-button-ghost tw-lf-button-sm tw-relative
                  tw-inline-block tw-px-2.5 tw-py-2 desktop:tw-px-4 desktop:tw-py-2 desktop:tw-text-[16px]
                `}
              >
                {tabContent.props.label}
              </button>
            );
          })}
        </div>

        {React.Children.map(TabsContent, (tabContent: JSX.Element, index) =>
          React.cloneElement(tabContent, {
            tabId: index + 1,
            tabPrefix,
            isTabSelected: selectedTab === index + 1,
          })
        )}
      </div>
    </>
  );
};

export { TabPanel, Tabs };
